import type {ReactNode} from 'react'
import {Heading, Image, Link, River, Text, Stack} from '@primer/react-brand/esm'
import type {RiverProps} from '@primer/react-brand/esm'

import {getImageSources} from '@github-ui/swp-core/lib/utils/images'

import {AssetTypeEnum} from '../../types'
import type {Brand, LabelAssetLinkItem} from '../../types'
import {AssetVideo} from '../Video'
import {getAssetImageStyle, getBrandDitherColor, renderWithoutParagraphs, useDitherBackground} from '../../utils'
import styles from './FlexSuiteRiverStack.module.css'

export type FeaturedStackProps = {
  items?: LabelAssetLinkItem[]
  alignment?: 'alternate' | 'start' | 'end'
  variant?: RiverProps['variant']
  brand?: Brand
  showDither?: boolean
}

const RIVER_ALIGNMENTS: Array<RiverProps['align']> = ['start', 'end']

type DitherContainerRef = ReturnType<typeof useDitherBackground>['containerRef']

type RiverStackItemProps = {
  item: LabelAssetLinkItem
  align: RiverProps['align']
  variant: RiverProps['variant']
  brand?: Brand
  showDither?: boolean
}

function RiverItemMedia({item}: {item: LabelAssetLinkItem}) {
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource} = item.fields
  const imageStyle = getAssetImageStyle(optionImage)

  return (
    <>
      {assetType === AssetTypeEnum.enum.Image && optionImage && (
        <Image
          as="picture"
          aspectRatio={imageStyle ? 'custom' : undefined}
          src={`${optionImage.fields.file.url}?fm=webp`}
          sources={getImageSources(optionImage.fields.file.url, {maxWidth: 720})}
          style={imageStyle}
          alt={optionImage.fields.description ?? ''}
          loading="lazy"
        />
      )}
      <AssetVideo
        assetType={assetType}
        optionVideo={optionVideo}
        optionPoster={optionPoster}
        autoplay={autoplay}
        videoSource={videoSource}
      />
    </>
  )
}

function RiverStackItemContent({
  item,
  align,
  variant,
  containerRef,
  ditherBackground,
}: Omit<RiverStackItemProps, 'brand' | 'showDither'> & {
  containerRef?: DitherContainerRef
  ditherBackground?: ReactNode
}) {
  const {heading, description, href, label, alt, position} = item.fields

  const content = [
    <Heading key="heading" as="h3" size="5">
      {renderWithoutParagraphs(heading)}
    </Heading>,
    ...(description ? [<Text key="description">{renderWithoutParagraphs(description)}</Text>] : []),
  ]

  if (href && label) {
    content.push(
      <Link key="link" href={href} {...(alt ? {'aria-label': alt} : {})}>
        {label}
      </Link>,
    )
  }

  return (
    <River align={align} variant={variant}>
      <River.Visual ref={containerRef} fillMedia={false} className={styles.visual}>
        <div className={styles.mediaFrame} data-image-position={position}>
          {ditherBackground}
          <RiverItemMedia item={item} />
        </div>
      </River.Visual>
      <River.Content>{content}</River.Content>
    </River>
  )
}

function RiverStackItem({showDither = false, brand, ...props}: RiverStackItemProps) {
  const isFill = props.item.fields.position === 'fill'
  const {containerRef, ditherBackground} = useDitherBackground({
    className: styles.dither,
    enabled: showDither && !isFill,
    params: {color: getBrandDitherColor(brand), type: 'random', fadeIn: true},
  })

  return <RiverStackItemContent {...props} containerRef={containerRef} ditherBackground={ditherBackground} />
}

export const FlexSuiteRiverStack = ({
  items,
  alignment = 'start',
  variant = 'gridline',
  brand,
  showDither = false,
}: FeaturedStackProps) => {
  if (!items || items.length === 0) return null

  return (
    <Stack direction="vertical" padding="none" gap="spacious">
      {items.map((item, index) => {
        const align: RiverProps['align'] =
          alignment === 'alternate' ? RIVER_ALIGNMENTS[index % RIVER_ALIGNMENTS.length] : alignment

        return (
          <RiverStackItem
            key={item.sys.id}
            item={item}
            align={align}
            variant={variant}
            brand={brand}
            showDither={showDither}
          />
        )
      })}
    </Stack>
  )
}
